/**
 * Skeleton — web implementation, animated with CSS keyframes.
 *
 * `pulse` is a keyframed opacity on the surface itself; `wave` is a gradient
 * sheen child that translates across the surface. Both rules live in an
 * injected stylesheet (see `skeleton-web-styles.ts`) and are selected via
 * `data-*` attributes, which is how react-native-web lets us hand styling off
 * to CSS. Doing it in CSS rather than JS means an arbitrary number of
 * placeholders animate off the main thread and honour
 * `prefers-reduced-motion` without a listener.
 */
import { useInsertionEffect, type FC } from "react";
import type { ViewStyle } from "react-native";
import { View } from "../View";
import {
  PULSE_ATTRIBUTE,
  WAVE_ATTRIBUTE,
  injectSkeletonBaseStyles,
  injectSkeletonWaveGradient,
} from "./skeleton-web-styles";
import { SkeletonChildrenSlot, useSkeletonSharedState } from "./Skeleton.shared";
import type { SkeletonProps } from "./Skeleton.types";

/**
 * `dataSet` is a react-native-web extension that react-native's own types
 * don't declare, so it is passed as a spread rather than a literal prop.
 */
interface DataSetProps {
  readonly dataSet: Readonly<Record<string, string>>;
}

const sheenStyle: ViewStyle = { position: "absolute", top: 0, left: 0, right: 0, bottom: 0 };

const Skeleton: FC<SkeletonProps> = (props) => {
  const state = useSkeletonSharedState(props);
  const isWave = state.animation === "wave";
  const isPulse = state.animation === "pulse";

  useInsertionEffect(() => {
    injectSkeletonBaseStyles();
    if (isWave) injectSkeletonWaveGradient(state.highlightColor);
  }, [isWave, state.highlightColor]);

  const surfaceProps: DataSetProps | Record<string, never> = isPulse ? { dataSet: { [PULSE_ATTRIBUTE]: "pulse" } } : {};
  const sheenProps: DataSetProps = { dataSet: { [WAVE_ATTRIBUTE]: state.highlightColor } };

  return (
    <View
      style={[state.containerStyle, props.style]}
      {...surfaceProps}
      {...state.accessibilityProps}
      {...(state.testID !== undefined ? { testID: state.testID } : {})}
    >
      {isWave && <View style={sheenStyle} {...sheenProps} />}
      {state.hasChildren && <SkeletonChildrenSlot>{state.children}</SkeletonChildrenSlot>}
    </View>
  );
};

Skeleton.displayName = "Skeleton";

export { Skeleton };
